import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import APIRequest from "~/lib/api_request";
import LeadDrawerLabel from "./details/lead_drawer_label";
import LabelingForm from "./forms/labeling_form";

const LeadDrawerLabels = ({ lead = null}) => {
  const canManageLead = Rails.abilities.manageLead;
  const isLabelsSectionOpen = localStorage.isLabelsSectionOpen;
  const [isOpen, setIsOpen] = useState(
    isLabelsSectionOpen ? JSON.parse(isLabelsSectionOpen) : false,
  );
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLabels = useCallback(async () => {
    if (!lead?.id) {
      return;
    }

    setIsLoading(true);

    try {
      const request = APIRequest.get({
        resource: `/v1/leads/${lead.id}/labels`,
      });

      const response = await new Promise((resolve, reject) => {
        request.end((error, res) => {
          if (error) {
            reject(error);
          } else {
            resolve(res);
          }
        });
      });

      setLabels(response.body);
    } catch (error) {
      console.error("Error loading labels:", error);
    } finally {
      setIsLoading(false);
    }
  }, [lead?.id]);

  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      await fetchLabels();
    };

    loadData();

    return () => {
      abortController.abort();
    };
  }, [fetchLabels]);

  const handleLabelCreated = useCallback((newLabel) => {
    setLabels((prevLabels) => [newLabel, ...prevLabels]);
  }, []);

  const handleLabelRemoved = useCallback((removedLabel) => {
    setLabels((prevLabels) =>
      prevLabels.filter((label) => label.id !== removedLabel.id),
    );
  }, []);

  const onCollapseClick = useCallback(
    (e) => {
      e.preventDefault();
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      localStorage.isLabelsSectionOpen = newIsOpen;
    },
    [isOpen],
  );

  const renderLoading = () => (
    <div className="text-center card-block">
      <FontAwesomeIcon icon="far fa-spinner" pulse />
    </div>
  );

  const renderLeadLabels = () => {
    if (isLoading) {
      return renderLoading();
    }

    if (!labels || labels.length === 0) {
      return <div className="text-grey-dark card-block">No labels</div>;
    }

    return labels.map((label) => (
      <LeadDrawerLabel
        key={`label-${label.id}`}
        leadID={lead.id}
        label={label}
        onLabelRemoved={handleLabelRemoved}
      />
    ));
  };

  return (
    <>
      <div className="section-header-grid mb-2">
        <h3 className="mb-0 section-title">Labels</h3>

        {canManageLead && isOpen && (
          <div className="section-form-wrapper">
            <LabelingForm
              leadID={lead.id}
              onLabelCreated={handleLabelCreated}
            />
          </div>
        )}

        <div className="section-button-wrapper">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onCollapseClick}
          >
            {isOpen ? (
              <FontAwesomeIcon icon={["far", "fa-minus"]} />
            ) : (
              <FontAwesomeIcon icon={["far", "fa-plus"]} />
            )}
          </button>
        </div>
      </div>

      <Collapse in={isOpen} mountOnEnter unmountOnExit>
        <div className="labels mt-2">{renderLeadLabels()}</div>
      </Collapse>
    </>
  );
};

LeadDrawerLabels.propTypes = {
  lead: PropTypes.shape({}),
};

export default LeadDrawerLabels;
